import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useLogout } from '../../hooks/useLogout'
import { useApiClient } from '../../utils/ApiClient'
import { AuthSessionContext } from '../../utils/AuthContext'
import { ClientApiClient } from '../../utils/clientApi'
import { addEventListeners, removeEventListeners } from '../../utils/eventListener.utils'
import { useLocallyPersistedState } from '../../utils/useStorage'

const REDIRECT_TIME = 5 * 60 * 1000 // 5 min
const WARN_AFTER_TIME = 15 * 60 * 1000 // 15 min

interface SessionTimeoutContextValue {
  warningModal: boolean
  onStayConnected: () => void
  onLogout: () => void
  timer: number
  redirectAfter: number
}

const SessionTimeoutContext = createContext<SessionTimeoutContextValue | null>(null)

export function useSessionTimeout(): SessionTimeoutContextValue | null {
  return useContext(SessionTimeoutContext)
}

export function SessionTimeoutProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [auth] = useContext(AuthSessionContext)
  const { logout } = useLogout()
  const clientApi = useApiClient(ClientApiClient)

  const [warningModal, setWarningModal] = useLocallyPersistedState<boolean>('sessionWarning', false)

  const [lastActivity, setLastActivity] = useLocallyPersistedState<number>(
    'lastActivityTimestamp',
    Date.now()
  )

  const [timer, setTimer] = useLocallyPersistedState<number>('sessionTimer', REDIRECT_TIME)
  const [modalStartTime, setModalStartTime] = useState<number | null>(null)

  const resetTimers = useCallback(() => {
    setTimer(REDIRECT_TIME)
    setWarningModal(false)
    setLastActivity(Date.now())
    setModalStartTime(null)
  }, [setLastActivity, setTimer, setWarningModal, setModalStartTime])

  const onLogout = useCallback(async () => {
    const currentAuth = auth
    if (currentAuth) {
      await clientApi.logOut(currentAuth.id)
    }
    logout()

    resetTimers()
  }, [auth, clientApi, logout, resetTimers])

  const onStayConnected = useCallback(() => {
    resetTimers()
  }, [resetTimers])

  useEffect(() => {
    const handleActivity = () => {
      if (!warningModal) {
        setLastActivity(Date.now())
      }
    }

    addEventListeners(handleActivity)
    return () => {
      removeEventListeners(handleActivity)
    }
  }, [auth, warningModal, setLastActivity])

  useEffect(() => {
    if (!auth) {
      setWarningModal(false)
      return
    }

    const interval = setInterval(() => {
      const now = Date.now()

      if (!warningModal) {
        const inactivity = now - lastActivity
        if (inactivity > WARN_AFTER_TIME) {
          setWarningModal(true)
          setModalStartTime(now)
        }
      } else {
        if (modalStartTime !== null) {
          const countdown = REDIRECT_TIME - (now - modalStartTime)
          if (countdown > 0) {
            setTimer(countdown)
          } else {
            onLogout()
            setTimer(0)
          }
        }
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [auth, warningModal, lastActivity, modalStartTime, setWarningModal, setTimer, onLogout])

  const value: SessionTimeoutContextValue = useMemo(
    () => ({
      warningModal,
      onStayConnected,
      onLogout,
      timer,
      redirectAfter: REDIRECT_TIME,
    }),
    [warningModal, onStayConnected, onLogout, timer]
  )

  return <SessionTimeoutContext.Provider value={value}>{children}</SessionTimeoutContext.Provider>
}
